import { ListJobsService } from "@application/useCases";
import { StatusEnumSchema } from "@domain/schemas/BI";
import { LogDeliveryMode, LogMessage, subscribeToBatchLogs, subscribeToLogMessages } from "@infrastructure/realtime/logsHubClient";
import LogsDialog from "@presentation/components/job/components/LogsDialog";
import React, { useEffect, useRef, useState } from "react";

type BatchArtifactsTarget = {
  jobId: string;
  id: string;
  jobPipelineStepId: string;
  status?: StatusEnumSchema | null;
};

type ArtifactMode = "logs" | "results";

export const useBatchArtifactsViewer = (listJobTasksService: ListJobsService) => {
  const [selectedBatch, setSelectedBatch] = useState<BatchArtifactsTarget | null>(null);
  const [isLogsDialogVisible, setIsLogsDialogVisible] = useState(false);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [logContent, setLogContent] = useState("");
  const selectedBatchRef = useRef<BatchArtifactsTarget | null>(null);
  const selectedSessionIdRef = useRef<string | null>(null);
  const selectedModeRef = useRef<ArtifactMode | null>(null);
  const unsubscribeBatchLogsRef = useRef<(() => void) | null>(null);

  const getOrCreateSessionId = () => {
    const key = "my-app-session-id";
    let sessionId = sessionStorage.getItem(key);
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(key, sessionId);
    }
    return sessionId;
  };

  const normalizeDeliveryMode = (deliveryMode?: LogDeliveryMode) => {
    switch (deliveryMode) {
      case "Append":
      case 1:
        return "Append";
      case "Prepend":
      case 2:
        return "Prepend";
      case "Replace":
      case 0:
      default:
        return "Replace";
    }
  };

  const applyLogMessage = (previous: string, logs: LogMessage) => {
    switch (normalizeDeliveryMode(logs.deliveryMode)) {
      case "Prepend":
        return previous ? `${logs.content}\n${previous}` : logs.content;
      case "Append":
        return previous ? `${previous}\n${logs.content}` : logs.content;
      case "Replace":
      default:
        return logs.content;
    }
  };

  const stopActiveStream = async () => {
    unsubscribeBatchLogsRef.current?.();
    unsubscribeBatchLogsRef.current = null;

    if (selectedBatchRef.current && selectedSessionIdRef.current && selectedModeRef.current === "logs") {
      await listJobTasksService.stopLogsAudit(
        selectedBatchRef.current.jobId,
        selectedBatchRef.current.jobPipelineStepId,
        selectedBatchRef.current.id,
        selectedSessionIdRef.current,
      ).catch(console.error);
    }

    selectedBatchRef.current = null;
    selectedSessionIdRef.current = null;
    selectedModeRef.current = null;
  };

  const startStream = (target: BatchArtifactsTarget, mode: ArtifactMode, sessionId: string) => {
    unsubscribeBatchLogsRef.current = subscribeToBatchLogs({
      jobId: target.jobId,
      jobPipelineStepId: target.jobPipelineStepId,
      batchId: target.id,
      sessionId,
    });

    selectedBatchRef.current = target;
    selectedSessionIdRef.current = sessionId;
    selectedModeRef.current = mode;
  };

  const openLogs = async (jobId: string, jobPipelineStepId: string, batch: Omit<BatchArtifactsTarget, "jobId" | "jobPipelineStepId">) => {
    await stopActiveStream();

    const target: BatchArtifactsTarget = { ...batch, jobId, jobPipelineStepId };
    const sessionId = getOrCreateSessionId();
    setLogContent("");
    setSelectedBatch(target);
    setIsResultsVisible(false);
    setIsLogsDialogVisible(true);

    startStream(target, "logs", sessionId);
    await listJobTasksService.startLogsAudit(target.jobId, target.jobPipelineStepId, target.id, sessionId);
  };

  const openResults = async (jobId: string, jobPipelineStepId: string, batch: Omit<BatchArtifactsTarget, "jobId" | "jobPipelineStepId">) => {
    await stopActiveStream();

    const target: BatchArtifactsTarget = { ...batch, jobId, jobPipelineStepId };
    const sessionId = getOrCreateSessionId();
    setLogContent("");
    setSelectedBatch(target);
    setIsLogsDialogVisible(false);
    setIsResultsVisible(true);

    startStream(target, "results", sessionId);
    await listJobTasksService.getResults(target.jobId, target.jobPipelineStepId, target.id, sessionId);
  };

  const closeDialog = async () => {
    setIsLogsDialogVisible(false);
    setIsResultsVisible(false);

    await stopActiveStream();
    setLogContent("");
    setSelectedBatch(null);
  };

  useEffect(() => {
    getOrCreateSessionId();

    const unsubscribeFromLogMessages = subscribeToLogMessages((logs) => {
      if (!selectedBatchRef.current || !selectedSessionIdRef.current) {
        return;
      }

      if (logs.batchId !== selectedBatchRef.current.id || logs.sessionId !== selectedSessionIdRef.current) {
        return;
      }

      setLogContent((previous) => applyLogMessage(previous, logs));
    });

    return () => {
      unsubscribeFromLogMessages();
      void stopActiveStream();
    };
  }, []);

  const artifactsDialog =
    isResultsVisible && selectedBatch ? (
      <LogsDialog logContent={logContent} onClose={closeDialog} isResult={true} isRunning={false} />
    ) : isLogsDialogVisible && selectedBatch ? (
      <LogsDialog logContent={logContent} onClose={closeDialog} isResult={false} isRunning={selectedBatch.status === StatusEnumSchema.Running} focusLastLineOnOpen={true} />
    ) : null;

  return {
    artifactsDialog,
    openLogs,
    openResults,
  };
};
